// Weight inspector — one big number, ±0.1 nudges, explicit save. Past dates
// edit exactly like today (no archive mode).

import { useEffect, useState } from 'react';
import { formatApiError } from '../../lib/api';
import { isoToday, useDayData } from '../../lib/day-data';
import { C, FONT_DISPLAY } from '../../lib/design';
import { dayLabel, fmt1, fmtDelta } from '../../lib/ledger';
import { DEFAULT_PROFILE } from '../../lib/types';
import { SurfaceComments } from '../day/SurfaceComments';
import { InspHead } from './InspHead';
import { Btn, ErrorStrip, Sub } from './ui';

function parseKg(raw: string): number | null {
  const n = Number(raw.trim().replace(',', '.'));
  return raw.trim() && Number.isFinite(n) && n > 0 ? Math.round(n * 10) / 10 : null;
}

export function WeightInspector({ date }: { date: string }) {
  const day = useDayData(date);
  const saved = day.weightToday;
  // Prefill from the last weight logged before this date, however long ago —
  // the delta line says how stale it is. Before any log, the profile's start
  // weight; never a hard-coded number.
  const prevRow = day.sortedWeights.filter((r) => r.date < date).at(-1) ?? null;
  const prev = prevRow?.weight_kg ?? null;
  const initial = saved ?? prev ?? day.profile.weight_start_kg ?? DEFAULT_PROFILE.weight_start_kg!;

  const [raw, setRaw] = useState(fmt1(initial));
  useEffect(() => {
    setRaw(fmt1(initial));
  }, [date, initial]);

  const value = parseKg(raw);
  const dirty = value != null && value !== saved;
  const weightMut = day.mutations.weight;
  const deleteMut = day.mutations.weightDelete;
  const pending = weightMut.isPending || deleteMut.isPending;
  const nudge = (d: number) => setRaw(fmt1((value ?? initial) + d));

  return (
    <div>
      <InspHead date={date} title="Morning weight" />
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Btn onClick={() => nudge(-0.1)} style={{ padding: '14px 16px', fontSize: 14 }} ariaLabel="Minus 0.1 kg">
            −
          </Btn>
          <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
            <input
              className="num"
              type="text"
              inputMode="decimal"
              aria-label="Weight in kg"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              style={{
                width: '100%',
                textAlign: 'center',
                background: 'transparent',
                border: 0,
                borderBottom: `1px solid ${C.line}`,
                color: value == null ? C.red : C.text,
                fontFamily: FONT_DISPLAY,
                fontSize: 48,
                outline: 'none',
                padding: 0,
              }}
            />
            <Sub style={{ marginTop: 6 }}>KG · FASTED, BEFORE COFFEE</Sub>
          </div>
          <Btn onClick={() => nudge(0.1)} style={{ padding: '14px 16px', fontSize: 14 }} ariaLabel="Plus 0.1 kg">
            +
          </Btn>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 18,
            borderTop: `1px solid ${C.line}`,
            paddingTop: 12,
          }}
        >
          <Sub>PREVIOUS · {prevRow ? `${fmt1(prevRow.weight_kg)} KG · ${dayLabel(prevRow.date)}` : '—'}</Sub>
          {prev != null && value != null && (
            <Sub color={value - prev <= 0 ? C.signal : C.warn} className="num">
              {fmtDelta(value - prev)} KG
            </Sub>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <Btn
            primary
            disabled={!dirty || pending}
            onClick={() => value != null && weightMut.mutate({ date, weight_kg: value })}
            style={{ flex: 1 }}
          >
            {weightMut.isPending ? 'SAVING…' : saved == null ? 'LOG WEIGHT' : 'SAVE CORRECTION'}
          </Btn>
          {saved != null && (
            <Btn danger disabled={pending} onClick={() => deleteMut.mutate({ date })}>
              CLEAR
            </Btn>
          )}
        </div>
        {(weightMut.error || deleteMut.error) && (
          <div style={{ marginTop: 8 }}>
            <ErrorStrip message={formatApiError(weightMut.error ?? deleteMut.error)} />
          </div>
        )}

        <div style={{ marginTop: 16, border: `1px dashed ${C.line}`, padding: '4px 12px 10px' }}>
          <SurfaceComments date={date} surface="weight" isToday={date === isoToday()} />
        </div>
      </div>
    </div>
  );
}
